import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Video, Square, Loader2, Sparkles, RotateCcw, Play, Lock } from "lucide-react";

const CHUNK = 5 * 1024 * 1024;
const LIMITS = {
  gameplay: { bytes: 1024 * 1024 * 1024, seconds: 935, recordMs: 900000, label: "15 minutes", sizeLabel: "1GB" },
  drill: { bytes: 100 * 1024 * 1024, seconds: 185, recordMs: 180000, label: "3 minutes", sizeLabel: "100MB" },
};

export default function VideoAnalyzer({ kind, drill, compact = false, onComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tier = user?.membership || "free";
  const [freeUsed, setFreeUsed] = useState(true);
  const [drillRate, setDrillRate] = useState(null);
  useEffect(() => {
    if (tier === "free" && kind === "gameplay") {
      api.get("/videos/analyses", { params: { kind: "gameplay" } })
        .then(({ data }) => setFreeUsed(data.some((a) => a.score != null))).catch(() => {});
    }
    if (kind === "drill") {
      api.get("/videos/pricing").then(({ data }) => setDrillRate(data.your_rate)).catch(() => {});
    }
  }, []); // eslint-disable-line
  const freeAssessment = tier === "free" && kind === "gameplay" && !freeUsed;
  const canUpload = kind === "drill" || tier === "vip" || tier === "ace" || tier === "goat" || freeAssessment;
  const canRecord = tier === "ace" || tier === "goat";
  const [phase, setPhase] = useState("idle"); // idle | recording | ready | uploading | analyzing | done | failed
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [playerDesc, setPlayerDesc] = useState("");
  const [showTips, setShowTips] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const liveRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => { stopStream(); if (previewUrl) URL.revokeObjectURL(previewUrl); }, []); // eslint-disable-line

  const stopStream = () => {
    clearTimeout(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const limits = LIMITS[kind] || LIMITS.drill;

  const setVideoBlob = (b, name) => {
    if (b.size > limits.bytes) return toast.error(`Video is over the ${limits.sizeLabel} limit`);
    const url = URL.createObjectURL(b);
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.onloadedmetadata = () => {
      if (isFinite(probe.duration) && probe.duration > limits.seconds) {
        URL.revokeObjectURL(url);
        return toast.error(`Keep ${kind === "gameplay" ? "game film" : "drill videos"} under ${limits.label} for analysis`);
      }
      setBlob(Object.assign(b, { _name: name }));
      setPreviewUrl(url);
      setPhase("ready");
    };
    probe.onerror = () => { setBlob(Object.assign(b, { _name: name })); setPreviewUrl(url); setPhase("ready"); };
    probe.src = url;
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setVideoBlob(f, f.name);
    e.target.value = "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setPhase("recording");
      setTimeout(() => { if (liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.play().catch(() => {}); } }, 50);
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      rec.onstop = () => {
        stopStream();
        setVideoBlob(new Blob(chunks, { type: rec.mimeType || "video/webm" }), "live-recording.webm");
      };
      recorderRef.current = rec;
      rec.start();
      timerRef.current = setTimeout(() => { if (rec.state === "recording") rec.stop(); toast.info(`${limits.label} limit reached — recording stopped`); }, limits.recordMs);
    } catch {
      toast.error("Camera access denied — allow camera & microphone to record live");
    }
  };

  const stopRecording = () => { if (recorderRef.current?.state === "recording") recorderRef.current.stop(); };

  const reset = () => {
    stopStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setBlob(null); setPreviewUrl(null); setResult(null); setError(""); setProgress(0);
    setPhase("idle");
  };

  const submit = async () => {
    if (!blob) return;
    if (kind === "gameplay" && !playerDesc.trim()) {
      return toast.error("Tell the AI which player you are (e.g. \"red shirt, near side left\")");
    }
    setPhase("uploading"); setProgress(0);
    const uploadId = `v${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    const total = Math.ceil(blob.size / CHUNK);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", blob._name || "video.mp4");
        fd.append("chunk", blob.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post("/videos/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      setPhase("analyzing");
      const { data } = await api.post("/videos/analyze", {
        upload_id: uploadId, kind, drill_id: drill?.id || null, mime_type: blob.type || "video/mp4",
        player_desc: kind === "gameplay" ? playerDesc.trim() : null,
      });
      poll(data.analysis_id, 0);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Upload failed");
      setPhase("failed");
    }
  };

  const poll = async (id, attempt) => {
    if (attempt > 240) { setError("Analysis timed out — check back later."); setPhase("failed"); return; }
    try {
      const { data } = await api.get(`/videos/analyses/${id}`);
      if (data.status === "complete") {
        setResult(data); setPhase("done");
        if (tier === "free") setFreeUsed(true);
        toast.success(kind === "drill" ? `Drill scored: ${data.score}/100` : `Game film scored: ${data.score}/100`);
        onComplete?.();
        return;
      }
      if (data.status === "failed") { setError(data.error || "Analysis failed"); setPhase("failed"); return; }
      setTimeout(() => poll(id, attempt + 1), 3000);
    } catch { setTimeout(() => poll(id, attempt + 1), 3000); }
  };

  const tid = kind === "drill" ? `drill-${drill?.id}` : "gameplay";

  if (phase === "done" && result) {
    return (
      <div data-testid={`video-result-${tid}`} className={`border p-4 ${kind === "drill" && result.passed != null ? (result.passed ? "border-[#A3E635]/60 bg-[#A3E635]/5" : "border-amber-500/50 bg-amber-500/5") : "border-pb-cyan/40 bg-pb-cyan/5"}`}>
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <span className="font-display font-black text-3xl text-pb-red">{result.score}<span className="text-sm text-zinc-500">/100</span></span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-pb-cyan">{kind === "drill" ? "AI Drill Score" : "AI Game Film Score"}</span>
          {kind === "drill" && result.passed != null && (
            <span data-testid={`drill-result-${result.passed ? "passed" : "failed"}`}
              className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 border ${result.passed ? "text-[#A3E635] border-[#A3E635]/60 bg-[#A3E635]/10" : "text-amber-400 border-amber-500/50 bg-amber-500/10"}`}>
              {result.passed ? "✓ PASSED — counts toward PSI" : `Not passed — standard ${result.pass_score}/100`}
            </span>
          )}
          <button data-testid={`video-again-${tid}`} onClick={reset} className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-pb-red"><RotateCcw className="w-3 h-3" /> {kind === "drill" && result.passed === false ? "Resubmit" : "New video"}</button>
        </div>
        {kind === "gameplay" && result.player_identified && (
          <div className="text-[11px] text-pb-cyan mb-2" data-testid="video-player-identified">AI analyzed: {result.player_identified}</div>
        )}
        {kind === "gameplay" && <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-pb-red mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> The One Factor To Level Up</div>}
        <p className="text-sm text-zinc-300 leading-relaxed">{result.feedback}</p>
        {kind === "drill" && (result.done_well?.length > 0 || result.improve?.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            {result.done_well?.length > 0 && (
              <div data-testid="drill-done-well">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3E635] mb-1">Executed correctly</div>
                <ul className="text-xs text-zinc-400 space-y-1">{result.done_well.map((x) => <li key={x}>✓ {x}</li>)}</ul>
              </div>
            )}
            {result.improve?.length > 0 && (
              <div data-testid="drill-improve">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-400 mb-1">To pass, improve</div>
                <ul className="text-xs text-zinc-400 space-y-1">{result.improve.map((x) => <li key={x}>→ {x}</li>)}</ul>
              </div>
            )}
          </div>
        )}
        {kind === "drill" && result.next_drills?.length > 0 && (
          <div className="mt-3 border-t border-pb-border/60 pt-3" data-testid="drill-next-recs">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-pb-cyan mb-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Progression — Highest-impact next drills</div>
            <ul className="text-xs text-zinc-400 space-y-1">
              {result.next_drills.map((r) => <li key={r.id}><b className="text-zinc-200">{r.name}</b> <span className="text-zinc-500">({r.level})</span> — {r.reason}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`video-analyzer-${tid}`}>
      {phase === "idle" && (
        <div className="flex flex-wrap gap-2">
          <button data-testid={`video-upload-btn-${tid}`}
            onClick={() => {
              if (!canUpload) {
                navigate(`/upgrade?reason=${encodeURIComponent("You've used your free film assessment — VIP and ACE members get unlimited AI game-film analysis.")}`);
                return;
              }
              fileRef.current?.click();
            }}
            className={`flex items-center gap-2 border font-bold uppercase tracking-widest transition-colors ${compact ? "px-3 py-2 text-[10px]" : "px-4 py-2.5 text-xs"} ${canUpload ? "bg-pb-elevated border-pb-border hover:border-pb-red" : "bg-pb-elevated/50 border-pb-border text-zinc-500 hover:border-pb-red"}`}>
            {canUpload ? <Upload className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} /> : <Lock className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />} Upload Video
            {freeAssessment && <span data-testid="free-assessment-badge" className="text-[9px] text-pb-red font-mono normal-case tracking-normal">1 FREE</span>}
            {!canUpload && <span className="text-[9px] text-pb-red font-mono normal-case tracking-normal">VIP+</span>}
            {kind === "drill" && drillRate != null && (
              <span data-testid={`drill-rate-badge-${tid}`} className={`text-[9px] font-mono normal-case tracking-normal ${drillRate > 0 ? "text-amber-400" : "text-[#A3E635]"}`}>
                {drillRate > 0 ? `${drillRate} BLC` : "FREE"}
              </span>
            )}
          </button>
          <button data-testid={`video-record-btn-${tid}`}
            onClick={() => {
              if (!canRecord) {
                navigate(`/upgrade?reason=${encodeURIComponent("Record LIVE AI analysis is an ACE exclusive — upgrade to go live.")}`);
                return;
              }
              startRecording();
            }}
            className={`flex items-center gap-2 border font-bold uppercase tracking-widest transition-colors ${compact ? "px-3 py-2 text-[10px]" : "px-4 py-2.5 text-xs"} ${canRecord ? "bg-pb-elevated border-pb-border hover:border-pb-cyan" : "bg-pb-elevated/50 border-pb-border text-zinc-500 hover:border-pb-cyan"}`}>
            {canRecord ? <Video className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} /> : <Lock className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />} Record Live
            {!canRecord && <span className="text-[9px] text-pb-red font-mono normal-case tracking-normal">ACE</span>}
          </button>
          <input ref={fileRef} type="file" accept="video/*" onChange={onFile} className="hidden" data-testid={`video-file-input-${tid}`} />
        </div>
      )}

      {phase === "recording" && (
        <div>
          <video ref={liveRef} muted playsInline className="w-full max-h-64 bg-black border border-pb-red mb-2" />
          <button data-testid={`video-stop-btn-${tid}`} onClick={stopRecording}
            className="flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest px-4 py-2.5 text-xs hover:bg-red-500 animate-pulse">
            <Square className="w-4 h-4" /> Stop Recording
          </button>
        </div>
      )}

      {phase === "ready" && (
        <div>
          <video src={previewUrl} controls playsInline className="w-full max-h-64 bg-black border border-pb-border mb-2" />
          {kind === "gameplay" && (
            <div className="mb-3">
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Which player are you? *</label>
              <input data-testid="video-player-desc" value={playerDesc} onChange={(e) => setPlayerDesc(e.target.value)}
                placeholder='e.g. "red shirt, near side left" or "blue hat, far right"'
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
              <p className="text-[10px] text-zinc-600 mt-1">The AI will find and grade ONLY you in the film.</p>
            </div>
          )}
          <div className="flex gap-2">
            <button data-testid={`video-submit-btn-${tid}`} onClick={() => setShowTips(true)}
              className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest px-5 py-2.5 text-xs hover:bg-[#BEF264] active:scale-95 transition-all">
              <Play className="w-4 h-4" /> Submit for AI Analysis{kind === "drill" && drillRate != null && (drillRate > 0 ? ` · ${drillRate} BLC` : " · Free")}
            </button>
            <button onClick={reset} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-pb-red"><RotateCcw className="w-3 h-3" /> Discard</button>
          </div>
        </div>
      )}

      {showTips && (
        <div className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowTips(false)}>
          <div className="bg-pb-surface border border-pb-border p-6 w-full max-w-md my-8" onClick={(e) => e.stopPropagation()} data-testid="video-tips-modal">
            <div className="flex items-center gap-2 mb-1">
              <Video className="w-4 h-4 text-pb-red" />
              <span className="font-display font-black text-lg uppercase">Get a grade, not a wasted upload</span>
            </div>
            <p className="text-xs text-zinc-500 mb-4">The AI can only score what it can clearly see. Quick check before you submit:</p>
            <ul className="space-y-2.5 mb-4">
              {[
                "Landscape (horizontal) video, held steady — prop your phone on a fence or tripod, no zooming or panning",
                "Your FULL body and the court in frame the entire time — filmed from behind or beside the court",
                "Good light: avoid shooting into the sun or dark indoor shadows",
                kind === "gameplay"
                  ? "Multiple players in frame? Describe yourself (shirt color + side) so the AI grades ONLY you"
                  : "Show the complete drill from start to finish — reps the AI can't see don't count",
                `Keep it under ${limits.label} of actual play — trim warm-ups and dead time before uploading`,
              ].map((t, i) => (
                <li key={i} className="flex gap-2.5 text-xs text-zinc-300">
                  <span className="shrink-0 w-4 h-4 mt-[1px] bg-pb-red/15 border border-pb-red/40 text-pb-red text-[9px] font-black flex items-center justify-center">{i + 1}</span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="border border-pb-cyan/40 bg-pb-cyan/5 px-3 py-2.5 mb-4 text-[11px] text-pb-cyan" data-testid="video-tips-credit-note">
              You're only charged when the AI returns a score. If the analysis fails, your coins are auto-refunded
              {kind === "gameplay" ? " and your free assessment is NOT used up." : "."}
            </div>
            <div className="flex gap-2">
              <button data-testid="video-tips-confirm" onClick={() => { setShowTips(false); submit(); }}
                className="flex-1 flex items-center justify-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest py-2.5 text-xs hover:bg-[#BEF264] active:scale-95 transition-all">
                <Play className="w-3.5 h-3.5" /> Looks good — Submit
              </button>
              <button data-testid="video-tips-cancel" onClick={() => setShowTips(false)}
                className="border border-pb-border text-zinc-300 px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:border-red-500 hover:text-red-400">
                Not yet
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === "uploading" && (
        <div data-testid={`video-uploading-${tid}`}>
          <div className="text-xs text-zinc-400 mb-2">Uploading… {progress}%</div>
          <div className="h-2 bg-pb-elevated"><div className="h-full bg-pb-red transition-all" style={{ width: `${progress}%` }} /></div>
        </div>
      )}

      {phase === "analyzing" && (
        <div className="flex items-center gap-3 text-sm text-zinc-300" data-testid={`video-analyzing-${tid}`}>
          <Loader2 className="w-5 h-5 text-pb-cyan animate-spin" />
          <div>
            <div className="font-display font-bold uppercase text-pb-cyan">AI is watching your film…</div>
            <div className="text-xs text-zinc-500">Grading against {kind === "drill" ? "the drill parameters" : "gameplay factors"} — usually under 2 minutes.</div>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="text-sm" data-testid={`video-failed-${tid}`}>
          <span className="text-red-400">{error}</span>
          <button onClick={reset} className="ml-3 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-pb-red">Try again</button>
        </div>
      )}
    </div>
  );
}
